import { useMemo } from 'react'

import {
  useListIdentitiesByIdsQuery,
  useListStaffersQuery,
  useSearchContactsQuery,
} from '~redux/slices/apiSlice'
import { type Identity2, type IdentityId } from '~types'

interface UseListDetailedIdentitiesArgs {
  user_id: string
  organization_identity_id_list: IdentityId[]
  limit: number
  query: string
  book_identity_id?: IdentityId
  // When true, only contacts are returned and staffers are not queried
  exclude_staff?: boolean
}

interface UseListDetailedIdentitiesResult {
  data: { identities: Identity2[] } | undefined
  isFetching: boolean
}

export const useListDetailedIdentities = (
  args: UseListDetailedIdentitiesArgs,
  options: { skip?: boolean } = {},
): UseListDetailedIdentitiesResult => {
  const skip = options.skip ?? false
  const orgId = args.organization_identity_id_list[0]

  const { data: staffersData, isFetching: staffersFetching } = useListStaffersQuery(
    {
      user_id: args.user_id,
      organization_identity_id_list: orgId ? [orgId] : [],
      limit: args.limit,
      query: args.query,
    },
    { skip: skip || !orgId || !!args.exclude_staff },
  )
  const stafferIds = staffersData?.identities.map((s) => s.id) ?? []

  const { data: stafferIdentitiesData, isFetching: stafferIdentitiesFetching } =
    useListIdentitiesByIdsQuery(
      { user_id: args.user_id, identity_ids: stafferIds },
      { skip: skip || stafferIds.length === 0 },
    )

  const { data: contactsData, isFetching: contactsFetching } = useSearchContactsQuery(
    {
      query: args.query,
      limit: args.limit,
      user_id: args.user_id,
      ...(args.book_identity_id ? { book_identity_id: args.book_identity_id } : {}),
    },
    { skip },
  )

  const data = useMemo(() => {
    if (skip) return undefined
    const contacts = contactsData?.contacts ?? []
    if (args.exclude_staff) return { identities: contacts.slice(0, args.limit) }
    const allStafferIdentity2s = stafferIdentitiesData?.identities ?? []
    const contactIds = new Set(contacts.map((c) => c.id))
    const stafferIdentity2s = allStafferIdentity2s.filter((s) => !contactIds.has(s.id))
    return { identities: [...contacts, ...stafferIdentity2s].slice(0, args.limit) }
  }, [skip, contactsData, stafferIdentitiesData, args.limit, args.exclude_staff])

  return {
    data,
    isFetching: staffersFetching || stafferIdentitiesFetching || contactsFetching,
  }
}
